/**
 * AdminSpendAnalysis — cross-vendor spend dashboard (super-admin only).
 *
 * Aggregates BOTH billed vendors in one place:
 *   - eCourts (case_detail, cause_list_search, cnr_causelist_batch, etc.)
 *   - Indian Kanoon (search, doc, docfragment, docmeta)
 *
 * Defaults to trailing 30 days. Date filters (from/to) + tenant filter
 * available. Compares against the previous same-length period for +/- % chips.
 *
 * Data source: single `/api/admin/spend-analysis` endpoint that reads both
 * `ecourts_passbook` and `indiankanoon_passbook` collections.
 */
import { useEffect, useMemo, useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import {
    ArrowDownRight, ArrowUpRight, IndianRupee, Zap, BookOpen,
    Calendar, TrendingUp, RefreshCw, Building2,
} from "lucide-react";

const rupee = (v) => `₹${(Number(v) || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
const num = (v) => Number(v || 0).toLocaleString("en-IN");
const today = () => new Date().toISOString().slice(0, 10);
const daysAgo = (n) => new Date(Date.now() - n * 86400000).toISOString().slice(0, 10);

const PRESETS = [
    { key: "7",  label: "7d",  days: 7  },
    { key: "30", label: "30d", days: 30 },
    { key: "90", label: "90d", days: 90 },
    { key: "365", label: "1y", days: 365 },
];

export default function AdminSpendAnalysis() {
    const [from, setFrom] = useState(daysAgo(30));
    const [to, setTo] = useState(today());
    const [tenantId, setTenantId] = useState("");
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/spend-analysis", {
                params: { from, to, ...(tenantId ? { tenant_id: tenantId } : {}) },
            });
            setData(data);
        } catch (e) { toast.error(apiError(e)); }
        finally { setLoading(false); }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { load(); }, []);

    const applyPreset = (days) => {
        setFrom(daysAgo(days - 1));
        setTo(today());
        setTimeout(() => load(), 0);
    };

    const maxDaily = useMemo(() => {
        if (!data?.daily_series?.length) return 1;
        return Math.max(1, ...data.daily_series.map(d => d.ecourts_billed + d.kanoon_billed));
    }, [data]);

    return (
        <div className="space-y-6" data-testid="admin-spend-analysis">
            <div>
                <div className="overline">Analytics · super-admin</div>
                <h1 className="text-2xl text-foreground mt-1">Vendor spend analysis</h1>
                <p className="text-xs text-muted-foreground mt-1 max-w-2xl">
                    Cross-vendor spend view — eCourts + Indian Kanoon combined. All figures in INR.
                    Trailing 30 days by default; use the presets or custom dates to slice differently.
                </p>
            </div>

            {/* Date filter bar */}
            <div className="bg-card border border-border p-4 flex flex-wrap items-end gap-3">
                <div className="flex gap-1">
                    {PRESETS.map(p => (
                        <button key={p.key} onClick={() => applyPreset(p.days)}
                            data-testid={`spend-preset-${p.key}`}
                            className="text-[10px] uppercase tracking-[0.15em] px-3 py-2 border border-border hover:border-primary hover:text-primary transition-colors">
                            {p.label}
                        </button>
                    ))}
                </div>
                <div className="text-xs space-y-1">
                    <div className="overline text-muted-foreground">From</div>
                    <input type="date" value={from} onChange={e => setFrom(e.target.value)}
                        data-testid="spend-from"
                        className="bg-background border border-border focus:border-primary outline-none px-3 py-2 text-sm"/>
                </div>
                <div className="text-xs space-y-1">
                    <div className="overline text-muted-foreground">To</div>
                    <input type="date" value={to} onChange={e => setTo(e.target.value)}
                        data-testid="spend-to"
                        className="bg-background border border-border focus:border-primary outline-none px-3 py-2 text-sm"/>
                </div>
                <div className="text-xs space-y-1 flex-1 min-w-[200px]">
                    <div className="overline text-muted-foreground">Tenant filter (optional)</div>
                    <input type="text" value={tenantId} onChange={e => setTenantId(e.target.value)}
                        placeholder="Tenant id — leave empty for all"
                        data-testid="spend-tenant"
                        className="w-full bg-background border border-border focus:border-primary outline-none px-3 py-2 text-sm font-mono"/>
                </div>
                <button onClick={load} disabled={loading} data-testid="spend-apply"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground hover:opacity-90 text-xs uppercase tracking-[0.15em] disabled:opacity-50">
                    {loading ? <RefreshCw className="w-3 h-3 animate-spin"/> : <RefreshCw className="w-3 h-3"/>}
                    Apply
                </button>
            </div>

            {data && (
                <>
                    {/* Summary tiles */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <SummaryTile label="Combined spend" value={rupee(data.totals.combined.billed)}
                            delta={data.totals.delta_pct.billed}
                            sub={`${num(data.totals.combined.calls)} calls · ${data.range.days}d window`}
                            icon={IndianRupee} tone="primary" testid="spend-tile-combined"/>
                        <SummaryTile label="Platform markup earned" value={rupee(data.totals.combined.markup)}
                            delta={data.totals.delta_pct.markup}
                            sub={`Base cost ₹${num(data.totals.combined.base.toFixed(2))} + fixed addon ₹${num(data.totals.combined.fixed_addon.toFixed(2))}`}
                            icon={TrendingUp} tone="emerald" testid="spend-tile-markup"/>
                        <SummaryTile label="Total API calls" value={num(data.totals.combined.calls)}
                            delta={data.totals.delta_pct.calls}
                            sub={`${num(data.totals.ecourts.failed_calls + data.totals.kanoon.failed_calls)} failed (not billed)`}
                            icon={Zap} tone="amber" testid="spend-tile-calls"/>
                    </div>

                    {/* Vendor split */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <VendorPanel title="eCourts" icon={Building2} tone="blue"
                            totals={data.totals.ecourts} previous={data.totals.previous_period.ecourts}
                            byEndpoint={data.ecourts_by_endpoint} testid="spend-panel-ecourts"/>
                        <VendorPanel title="Indian Kanoon" icon={BookOpen} tone="violet"
                            totals={data.totals.kanoon} previous={data.totals.previous_period.kanoon}
                            byEndpoint={data.kanoon_by_endpoint} testid="spend-panel-kanoon"/>
                    </div>

                    {/* Daily series chart (CSS-only stacked bars) */}
                    <div className="bg-card border border-border p-5">
                        <div className="flex items-center justify-between mb-4">
                            <div>
                                <div className="overline">Daily spend · both vendors stacked</div>
                                <div className="text-[11px] text-muted-foreground mt-0.5">
                                    <span className="inline-flex items-center gap-1"><span className="w-2 h-2 bg-blue-500 inline-block"/>eCourts</span>{" · "}
                                    <span className="inline-flex items-center gap-1"><span className="w-2 h-2 bg-violet-500 inline-block"/>Indian Kanoon</span>
                                </div>
                            </div>
                            <div className="text-[10px] text-muted-foreground">
                                Peak day: {rupee(maxDaily)}
                            </div>
                        </div>
                        <div className="flex items-end gap-0.5 h-40 overflow-x-auto" data-testid="spend-chart-daily">
                            {data.daily_series.map(d => {
                                const total = d.ecourts_billed + d.kanoon_billed;
                                const pct = (total / maxDaily) * 100;
                                const ecPct = total ? (d.ecourts_billed / total) * 100 : 0;
                                return (
                                    <div key={d.date} className="flex flex-col items-center gap-1 shrink-0 group relative" style={{ minWidth: "18px" }}>
                                        <div className="w-full flex flex-col justify-end" style={{ height: "9rem" }}>
                                            <div className="w-full flex flex-col justify-end" style={{ height: `${pct}%` }}>
                                                <div className="bg-violet-500 group-hover:bg-violet-400 transition-colors" style={{ height: `${100 - ecPct}%` }}/>
                                                <div className="bg-blue-500 group-hover:bg-blue-400 transition-colors" style={{ height: `${ecPct}%` }}/>
                                            </div>
                                        </div>
                                        {/* Tooltip on hover */}
                                        <div className="absolute -top-14 hidden group-hover:block bg-background border border-border px-2 py-1 text-[10px] whitespace-nowrap z-10">
                                            <div className="text-foreground">{d.date}</div>
                                            <div className="text-blue-400">eCourts {rupee(d.ecourts_billed)}</div>
                                            <div className="text-violet-400">IK {rupee(d.kanoon_billed)}</div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Top tenants */}
                    {data.top_tenants?.length > 0 && (
                        <div className="bg-card border border-border p-5">
                            <div className="overline mb-3">Top spending tenants · both vendors combined</div>
                            <div className="space-y-2">
                                {data.top_tenants.map((t, i) => (
                                    <div key={t.tenant_id} className="flex items-center gap-3 text-sm">
                                        <div className="w-6 text-[10px] text-muted-foreground">{i + 1}</div>
                                        <div className="flex-1 text-foreground truncate">{t.tenant_name}</div>
                                        <div className="text-[11px] text-muted-foreground">{num(t.calls)} calls</div>
                                        <div className="w-24 text-right font-mono text-primary">{rupee(t.billed)}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="text-[10px] text-muted-foreground/70 text-center pt-2 flex items-center justify-center gap-1">
                        <Calendar className="w-3 h-3"/>
                        Comparing {data.range.from} → {data.range.to} vs previous {data.range.days}d ({data.range.prev_from} → {data.range.prev_to})
                    </div>
                </>
            )}
        </div>
    );
}

function SummaryTile({ label, value, delta, sub, icon: Icon, tone = "primary", testid }) {
    const toneClass = {
        primary: "text-primary",
        emerald: "text-emerald-400",
        amber:   "text-amber-400",
    }[tone] || "text-primary";
    const positive = delta >= 0;
    return (
        <div className="bg-card border border-border p-5 hover:border-primary/40 transition-colors" data-testid={testid}>
            <div className="flex items-center justify-between mb-3">
                <div className="overline">{label}</div>
                <Icon className={`w-4 h-4 ${toneClass}`}/>
            </div>
            <div className={`text-3xl ${toneClass}`}>{value}</div>
            <div className="flex items-center gap-2 mt-2 text-[11px]">
                <span className={`inline-flex items-center gap-1 ${positive ? "text-emerald-400" : "text-rose-400"}`}>
                    {positive ? <ArrowUpRight className="w-3 h-3"/> : <ArrowDownRight className="w-3 h-3"/>}
                    {Math.abs(delta).toFixed(1)}%
                </span>
                <span className="text-muted-foreground">vs previous period</span>
            </div>
            <div className="text-[11px] text-muted-foreground mt-1">{sub}</div>
        </div>
    );
}

function VendorPanel({ title, icon: Icon, tone, totals, previous, byEndpoint, testid }) {
    const toneClass = tone === "blue" ? "text-blue-400" : "text-violet-400";
    const toneBorder = tone === "blue" ? "border-blue-500/30" : "border-violet-500/30";
    const delta = (previous?.billed || 0) === 0
        ? (totals.billed ? 100 : 0)
        : ((totals.billed - previous.billed) / previous.billed) * 100;
    const positive = delta >= 0;
    return (
        <div className={`bg-card border ${toneBorder} p-5`} data-testid={testid}>
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                    <Icon className={`w-4 h-4 ${toneClass}`}/>
                    <div className="overline">{title}</div>
                </div>
                <span className={`inline-flex items-center gap-1 text-[11px] ${positive ? "text-emerald-400" : "text-rose-400"}`}>
                    {positive ? <ArrowUpRight className="w-3 h-3"/> : <ArrowDownRight className="w-3 h-3"/>}
                    {Math.abs(delta).toFixed(1)}%
                </span>
            </div>
            <div className={`text-2xl ${toneClass} mb-1`}>{rupee(totals.billed)}</div>
            <div className="text-[11px] text-muted-foreground mb-4">
                {num(totals.calls)} calls · markup ₹{num(totals.markup.toFixed(2))} · {totals.failed_calls} failed
            </div>
            <div className="border-t border-border pt-3 space-y-1.5">
                <div className="overline text-muted-foreground text-[9px]">By endpoint</div>
                {byEndpoint.length === 0 ? (
                    <div className="text-[11px] text-muted-foreground/70">No calls in this window.</div>
                ) : (
                    byEndpoint.slice(0, 12).map(row => (
                        <div key={row.key} className="flex items-center gap-2 text-[11px]">
                            <div className="flex-1 min-w-0 truncate text-foreground">{row.label}</div>
                            <div className="text-muted-foreground shrink-0">{num(row.calls)}</div>
                            <div className="w-20 text-right font-mono text-primary">{rupee(row.billed)}</div>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
}
